/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { Card, FormRadio, StyledFlex, SwitchThemeContext, TextField } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-regular-svg-icons';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import ReCAPTCHA from 'react-google-recaptcha';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller, SubmitHandler } from 'src/common/form';
import { displayToast } from 'src/common/hooks';
import { GOOGLE_SITE_KEY } from 'src/common/constants';
import { ApiResponseStatus, ThemeColorMode } from 'src/common/enums';
import { useGetUserQuery } from 'src/common/slices';
import { StyledButton, StyledCrownSVG, StyledDivider, StyledForm, StyledSubscriptions, StyledIconButton, StyledInputsContainer, StyledLabel, StyledRadioGroup, StyledSlider, StyledSliderRow, StyledSummary, StyledSummaryWrapper, StyledValue, StyledVoucherText, StyledYourVoucherCard, StyledYourVoucherCardPrice, StyledYourVoucherCardTitle } from './voucher.styles';
import { Currency, VoucherFormData } from './voucher.types';
import { DEFAULT_SUBSCRIPTIONS, DEFAULT_MONTHS, EMPTY_RECAPTCHA_ERROR, INVALID_EMAIL_ERROR, EMPTY_EMAIL_ERROR, DEFAULT_AUTH_VALUES, DEFAULT_NO_AUTH_INPUT_VALUES, GBP, USD, GENERIC_ERROR } from './voucher.constants';
import { useGetBasePriceQuery, useStripeBuyVouchersMutation } from './voucher.slice';
import { calculatePrice } from './voucher.helpers';
export const Voucher: React.FC = () => {
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const [buyVouchers] = useStripeBuyVouchersMutation();
  const {
    data: basePriceData,
    isLoading: basePriceLoading
  } = useGetBasePriceQuery();
  const {
    isSuccess,
    isFetching: isFetchingGetUser,
    isLoading: isLoadingGetUser,
    data: user
  } = useGetUserQuery();
  const auth = isSuccess && user;
  const [subscriptions, setSubscriptions] = useState<number>(DEFAULT_SUBSCRIPTIONS);
  const [months, setMonths] = useState<number>(DEFAULT_MONTHS);
  const [currency, setCurrency] = useState<Currency | null>(GBP);
  const [isLoading, setIsLoading] = useState(false);
  const reCaptchaBypass = process.env.REACT_APP_RECAPTCHA_BYPASS === 'true';
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const buyVoucherFormValidationSchema = yup.object({
    subscriptions: yup.number().required(),
    months: yup.number().required(),
    email: auth ? yup.string().nullable() : yup.string().email(INVALID_EMAIL_ERROR).required(EMPTY_EMAIL_ERROR),
    currency: auth ? yup.mixed<Currency>().nullable().defined() : yup.mixed<Currency>().required().defined()
  });
  type BuyVoucherFromInputs = yup.InferType<typeof buyVoucherFormValidationSchema>;
  const [defaultValues, setDefaultValues] = useState<VoucherFormData>(auth ? DEFAULT_AUTH_VALUES : DEFAULT_NO_AUTH_INPUT_VALUES);
  const {
    control,
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: {
      errors
    }
  } = useForm<BuyVoucherFromInputs>({
    defaultValues,
    resolver: yupResolver(buyVoucherFormValidationSchema)
  });
  const {
    email: emailError
  } = errors;
  const handleBuy: SubmitHandler<VoucherFormData> = async (data: VoucherFormData) => {
    const reCaptcha = reCaptchaRef.current?.getValue();
    if (reCaptcha || reCaptchaBypass) {
      setIsLoading(true);
      try {
        const params = {
          email: data.email,
          months: data.months,
          subscriptions: data.subscriptions,
          currency: data.currency
        };
        const response = await buyVouchers(params).unwrap();
        if (response.status === ApiResponseStatus.SUCCESS && response.data?.url) {
          window.location.href = response.data.url;
          setSubscriptions(DEFAULT_SUBSCRIPTIONS);
          setMonths(DEFAULT_MONTHS);
          reCaptchaRef.current?.reset();
          clearErrors();
          reset(defaultValues);
        } else {
          displayToast('error', GENERIC_ERROR);
        }
      } catch {
        displayToast('error', GENERIC_ERROR);
      }
      setIsLoading(false);
    } else {
      displayToast('error', EMPTY_RECAPTCHA_ERROR);
    }
  };
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => setMonths(Number(e.target.value));
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCurrency((e.target.value as Currency));
    }
  };
  useEffect(() => {
    const newDefaultValues = auth ? {
      ...DEFAULT_AUTH_VALUES,
      currency: user?.data?.user?.local?.country === 'gb' ? GBP : USD
    } : DEFAULT_NO_AUTH_INPUT_VALUES;
    setCurrency(newDefaultValues.currency);
    setDefaultValues(newDefaultValues);
    reset(newDefaultValues);
  }, [auth, reset, user?.data?.user?.local?.country]);
  if (isFetchingGetUser || isLoadingGetUser || basePriceLoading) {
    return null;
  }
  return <Card padding="0" data-sentry-element="Card" data-sentry-component="Voucher" data-sentry-source-file="voucher.tsx">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <StyledForm onSubmit={handleSubmit(handleBuy)} data-sentry-element="StyledForm" data-sentry-source-file="voucher.tsx">
        <StyledYourVoucherCard data-sentry-element="StyledYourVoucherCard" data-sentry-source-file="voucher.tsx">
          <StyledYourVoucherCardTitle data-sentry-element="StyledYourVoucherCardTitle" data-sentry-source-file="voucher.tsx">Your voucher</StyledYourVoucherCardTitle>
          <StyledSliderRow data-sentry-element="StyledSliderRow" data-sentry-source-file="voucher.tsx">
            <p>Duration</p>
            <Controller name="months" control={control} render={({
            field: {
              onChange,
              value,
              ref,
              ...rest
            }
          }) => <StyledSlider type="range" min="1" max="24" step="1" aria-label="Duration in months" value={value ?? ''} onChange={e => {
            onChange(e);
            handleSliderChange(e);
          }} style={({
            '--value': `${(Number(value) - 1) / (24 - 1) * 100}%`
          } as React.CSSProperties)} {...rest} />} data-sentry-element="Controller" data-sentry-source-file="voucher.tsx" />
            <p>
              <span>{months}</span> months
            </p>
          </StyledSliderRow>
          <StyledFlex justifyContent="flex-start" data-sentry-element="StyledFlex" data-sentry-source-file="voucher.tsx">
            <StyledLabel data-sentry-element="StyledLabel" data-sentry-source-file="voucher.tsx">Quantity</StyledLabel>
            <StyledFlex gap="0.8rem" justifyContent="flex-start" width="fit-content" data-sentry-element="StyledFlex" data-sentry-source-file="voucher.tsx">
              <Controller name="subscriptions" control={control} render={({
              field: {
                onChange,
                value,
                ref,
                ...rest
              }
            }) => <>
                    <StyledIconButton variant="add" width="3.6rem" height="3.6rem" aria-label="Decrement subscriptions" onClick={() => {
                const newSubscriptions = value > 1 ? value - 1 : value;
                onChange(newSubscriptions);
                setSubscriptions(newSubscriptions);
              }} {...rest}>
                      <FontAwesomeIcon icon={faMinus} />
                    </StyledIconButton>
                    <StyledSubscriptions>
                      <p>{subscriptions}</p>
                    </StyledSubscriptions>
                    <StyledIconButton variant="add" width="3.6rem" height="3.6rem" aria-label="Increment subscriptions" onClick={() => {
                const newSubscriptions = value + 1;
                onChange(newSubscriptions);
                setSubscriptions(newSubscriptions);
              }} {...rest}>
                      <FontAwesomeIcon icon={faPlus} />
                    </StyledIconButton>
                  </>} data-sentry-element="Controller" data-sentry-source-file="voucher.tsx" />
            </StyledFlex>
            <StyledVoucherText data-sentry-element="StyledVoucherText" data-sentry-source-file="voucher.tsx">Voucher</StyledVoucherText>
          </StyledFlex>
          <StyledYourVoucherCardPrice data-sentry-element="StyledYourVoucherCardPrice" data-sentry-source-file="voucher.tsx">
            <h2>{calculatePrice(basePriceData?.data, months, subscriptions, currency)}</h2>
            {!auth && <StyledRadioGroup>
                <Controller name="currency" control={control} render={({
              field: {
                onChange,
                value,
                ref,
                ...rest
              }
            }) => <FormRadio isDark label="£ GBP" value={GBP} checked={currency === GBP} onChange={e => {
              onChange(e);
              handleRadioChange(e);
            }} {...rest} />} />
                <Controller name="currency" control={control} render={({
              field: {
                onChange,
                value,
                ref,
                ...rest
              }
            }) => <FormRadio isDark label="$ USD" value={USD} checked={currency === USD} onChange={e => {
              onChange(e);
              handleRadioChange(e);
            }} {...rest} />} />
              </StyledRadioGroup>}
          </StyledYourVoucherCardPrice>
          <StyledCrownSVG data-sentry-element="StyledCrownSVG" data-sentry-source-file="voucher.tsx" />
        </StyledYourVoucherCard>
        <StyledDivider data-sentry-element="StyledDivider" data-sentry-source-file="voucher.tsx" />
        <StyledSummaryWrapper data-sentry-element="StyledSummaryWrapper" data-sentry-source-file="voucher.tsx">
          <StyledSummary $isDarkMode={isDarkMode} data-sentry-element="StyledSummary" data-sentry-source-file="voucher.tsx">
            <p>You are buying </p>
            <StyledValue data-sentry-element="StyledValue" data-sentry-source-file="voucher.tsx">{subscriptions}</StyledValue>
            <p>subscription vouchers that have </p>
            <StyledValue data-sentry-element="StyledValue" data-sentry-source-file="voucher.tsx">{months}</StyledValue>
            <p>months expiry date (once redeemed), for a total of</p>
            <StyledValue data-sentry-element="StyledValue" data-sentry-source-file="voucher.tsx">{calculatePrice(basePriceData?.data, months, subscriptions, currency)}</StyledValue>
          </StyledSummary>
          <StyledInputsContainer data-sentry-element="StyledInputsContainer" data-sentry-source-file="voucher.tsx">
            {!auth && <Controller name="email" control={control} render={({
            field: {
              ref,
              value,
              ...rest
            }
          }) => <TextField {...register('email')} id="email" variant="input" label="Email address" labelPosition="left" aria-label="email address" placeholder="Enter email address" isError={!!emailError} errorMessage={emailError ? emailError.message : ''} innerRef={ref} value={value ?? ''} {...rest} />} />}
            <ReCAPTCHA key={theme} sitekey={GOOGLE_SITE_KEY} ref={reCaptchaRef} theme={theme} data-testid="recaptcha-comp" data-sentry-element="ReCAPTCHA" data-sentry-source-file="voucher.tsx" />
            <StyledButton type="submit" color="primary" variant="solid" isLoading={isLoading} aria-label="Buy with card submit" data-sentry-element="StyledButton" data-sentry-source-file="voucher.tsx">
              <FontAwesomeIcon icon={faCreditCard} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="voucher.tsx" />
              Buy with card
            </StyledButton>
          </StyledInputsContainer>
        </StyledSummaryWrapper>
      </StyledForm>
    </Card>;
};